import type { CatalogItem } from '@/entities/catalog-item';

import { ADDITIONAL_ITEM, spotColumns } from '@/features/catalog/constants';

export const PARTIAL_PERIOD_COUNT = 3;

export type PeriodColumn = {
  key: Extract<keyof CatalogItem, string>;
  label: string;
  width: number;
  align?: 'flex-start' | 'flex-end';
  kind?: 'number' | 'date';
  /**
   * Identity columns that name each period row — shown regardless of the main
   * table's column visibility (they are force-hidden there in "1 per code" mode,
   * but the sub-table is meaningless without them). Every other column mirrors
   * the parent's Display Settings visibility.
   */
  alwaysVisible?: boolean;
};

type SpotColumn = (typeof spotColumns)[number];

// Columns that belong only to the parent row (row selection, identity, status
// plumbing) — never repeated per period in the sub-table.
const PARENT_ONLY_KEYS = new Set<string>([
  'Symbol',
  'Factsheet',
  'Status',
  'LaunchDate',
  'Title',
  'Code',
  'Alias',
  ADDITIONAL_ITEM
]);

// Leading identity columns, in Figma order (spotColumns lists them reversed).
const IDENTITY_ORDER = ['Period', 'PeriodType', 'TimeRef'];

// Sub-table-specific presentation the parent column defs don't carry: number/date
// formatting, right-alignment for numeric values, and the Figma-tuned Last Date
// width. Everything else inherits the parent column's label (header) and size.
const PRESENTATION: Record<string, Partial<Pick<PeriodColumn, 'width' | 'align' | 'kind'>>> = {
  LastDate: { width: 110, kind: 'date' },
  Last: { align: 'flex-end', kind: 'number' },
  Change: { align: 'flex-end', kind: 'number' },
  StartDate: { kind: 'date' },
  EndDate: { kind: 'date' },
  Increment: { kind: 'number' }
};

const toPeriodColumn = (col: SpotColumn): PeriodColumn => {
  const key = col.accessorKey;
  const overrides = PRESENTATION[key];
  return {
    key,
    label: col.header,
    width: overrides?.width ?? ('size' in col ? (col.size ?? 120) : 120),
    align: overrides?.align,
    kind: overrides?.kind,
    alwaysVisible: IDENTITY_ORDER.includes(key)
  };
};

const findColumn = (key: string) => spotColumns.find(col => col.accessorKey === key);

const identityColumns = IDENTITY_ORDER.map(findColumn).filter((col): col is SpotColumn =>
  Boolean(col)
);

const dataColumns = spotColumns.filter(
  col => !PARENT_ONLY_KEYS.has(col.accessorKey) && !IDENTITY_ORDER.includes(col.accessorKey)
);

/**
 * Columns for the inline period sub-table, derived from the parent's `spotColumns`
 * so every toggleable Display Settings column exists here too. Identity columns
 * lead (Figma order); the rest follow the parent table's column order and are
 * shown/hidden by the same column-visibility state.
 */
export const PERIOD_COLUMNS: PeriodColumn[] = [
  ...identityColumns.map(toPeriodColumn),
  ...dataColumns.map(toPeriodColumn)
];
